import { useState } from 'react';
import { X, Moon, Flame, Brain, Activity, Save } from 'lucide-react';
import { 
  createSleepLog, 
  createPainLog, 
  createStressLog, 
  createVitalsLog 
} from '../../services/advancedTrackingService';

type TabType = 'sleep' | 'pain' | 'stress' | 'vitals';

interface AddTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  type: TabType;
}

export default function AddTrackingModal({ isOpen, onClose, onSuccess, type }: AddTrackingModalProps) {
  const [loading, setLoading] = useState(false);
  // Helper to get local date string (YYYY-MM-DD) instead of UTC
  const getLocalDateString = () => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  };

  const [formData, setFormData] = useState<any>({
    date: getLocalDateString(),
    quality: 'GOOD',
    painType: 'ACHING',
    mood: 'NEUTRAL',
    stressLevel: '',
    painLevel: '',
  });

  const config = {
    sleep: {
      icon: Moon,
      color: 'indigo',
      title: 'Adaugă Somn',
    },
    pain: {
      icon: Flame,
      color: 'red',
      title: 'Adaugă Durere',
    },
    stress: {
      icon: Brain,
      color: 'orange',
      title: 'Adaugă Stres',
    },
    vitals: {
      icon: Activity,
      color: 'emerald',
      title: 'Adaugă Vitale',
    },
  };

  const Icon = config[type].icon;
  const color = config[type].color;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Convert local date string to ISO with noon time to avoid timezone day shift
      // "2026-03-17" -> "2026-03-17T12:00:00" (local) -> correct UTC ISO string
      const date = new Date(formData.date + 'T12:00:00').toISOString();

      switch (type) {
        case 'sleep':
          await createSleepLog({
            date,
            duration: parseFloat(formData.duration),
            quality: formData.quality,
            interruptions: formData.interruptions ? parseInt(formData.interruptions) : undefined,
            notes: formData.notes || undefined,
          });
          break;
        case 'pain':
          await createPainLog({
            date,
            painLevel: parseInt(formData.painLevel),
            location: formData.location ? formData.location.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
            painType: formData.painType,
            triggers: formData.triggers ? formData.triggers.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
            notes: formData.notes || undefined,
          });
          break;
        case 'stress':
          await createStressLog({
            date,
            stressLevel: parseInt(formData.stressLevel),
            mood: formData.mood || 'NEUTRAL',
            symptoms: formData.symptoms ? formData.symptoms.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
            copingMethods: formData.copingMethods ? formData.copingMethods.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
            triggers: formData.stressTriggers ? formData.stressTriggers.split(',').map((s: string) => s.trim()).filter(Boolean) : [],
            notes: formData.notes || undefined,
          });
          break;
        case 'vitals':
          await createVitalsLog({
            date,
            glucose: formData.glucose ? parseFloat(formData.glucose) : undefined,
            systolicBP: formData.systolicBP ? parseInt(formData.systolicBP) : undefined,
            diastolicBP: formData.diastolicBP ? parseInt(formData.diastolicBP) : undefined,
            heartRate: formData.heartRate ? parseInt(formData.heartRate) : undefined,
            temperature: formData.temperature ? parseFloat(formData.temperature) : undefined,
            notes: formData.notes || undefined,
          });
          break;
      }

      onSuccess();
      onClose();
      setFormData({
        date: getLocalDateString(),
        quality: 'GOOD',
        painType: 'ACHING',
        mood: 'NEUTRAL',
        stressLevel: '',
        painLevel: '',
      });
    } catch (error) {
      console.error('Error creating log:', error);
      alert('Eroare la salvare');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full max-h-[92vh] sm:max-h-[90vh] overflow-y-auto" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 bg-${color}-100 rounded-xl`}>
              <Icon className={`w-5 h-5 text-${color}-600`} />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">{config[type].title}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
              required
            />
          </div>

          {type === 'sleep' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Durată (ore) *</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="24"
                  value={formData.duration || ''}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  placeholder="7.5"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Calitate</label>
                <select
                  value={formData.quality || 'GOOD'}
                  onChange={(e) => setFormData({ ...formData, quality: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="POOR">Slabă</option>
                  <option value="FAIR">Acceptabilă</option>
                  <option value="GOOD">Bună</option>
                  <option value="EXCELLENT">Excelentă</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Treziri în noapte</label>
                <input
                  type="number"
                  min="0"
                  value={formData.interruptions || ''}
                  onChange={(e) => setFormData({ ...formData, interruptions: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
                  placeholder="0"
                />
              </div>
            </>
          )}

          {type === 'pain' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nivel durere (1-10) *</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.painLevel || ''}
                  onChange={(e) => setFormData({ ...formData, painLevel: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Locație</label>
                <input
                  type="text"
                  value={formData.location || ''}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500"
                  placeholder="Ex: Cap, Spate, Articulații"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tip durere</label>
                <select
                  value={formData.painType || 'ACHING'}
                  onChange={(e) => setFormData({ ...formData, painType: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500"
                >
                  <option value="SHARP">Ascuțită</option>
                  <option value="DULL">Surdă</option>
                  <option value="BURNING">Arsură</option>
                  <option value="THROBBING">Pulsantă</option>
                  <option value="ACHING">Dureroasă</option>
                  <option value="STABBING">Înțepătoare</option>
                </select>
              </div>
            </>
          )}

          {type === 'stress' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nivel stres (1-10) *</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.stressLevel || ''}
                  onChange={(e) => setFormData({ ...formData, stressLevel: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Dispoziție</label>
                <select
                  value={formData.mood || 'NEUTRAL'}
                  onChange={(e) => setFormData({ ...formData, mood: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500"
                >
                  <option value="VERY_GOOD">Foarte bine</option>
                  <option value="GOOD">Bine</option>
                  <option value="NEUTRAL">Neutru</option>
                  <option value="BAD">Rău</option>
                  <option value="VERY_BAD">Foarte rău</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Simptome</label>
                <input
                  type="text"
                  value={formData.symptoms || ''}
                  onChange={(e) => setFormData({ ...formData, symptoms: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500"
                  placeholder="Ex: Tensiune musculară, Oboseală"
                />
              </div>
            </>
          )}

          {type === 'vitals' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Glicemie (mg/dL)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.glucose || ''}
                  onChange={(e) => setFormData({ ...formData, glucose: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500"
                  placeholder="90"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">TA Sistolică</label>
                  <input
                    type="number"
                    value={formData.systolicBP || ''}
                    onChange={(e) => setFormData({ ...formData, systolicBP: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500"
                    placeholder="120"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">TA Diastolică</label>
                  <input
                    type="number"
                    value={formData.diastolicBP || ''}
                    onChange={(e) => setFormData({ ...formData, diastolicBP: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500"
                    placeholder="80"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Puls (bpm)</label>
                <input
                  type="number"
                  value={formData.heartRate || ''}
                  onChange={(e) => setFormData({ ...formData, heartRate: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500"
                  placeholder="70"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Temperatură (°C)</label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.temperature || ''}
                  onChange={(e) => setFormData({ ...formData, temperature: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500"
                  placeholder="36.6"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notițe</label>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500"
              rows={3}
              placeholder="Observații..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
            >
              Anulează
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 px-4 py-2 bg-${color}-600 hover:bg-${color}-700 rounded-xl font-medium text-white flex items-center justify-center gap-2 disabled:opacity-50`}
            >
              <Save className="w-4 h-4" />
              {loading ? 'Salvează...' : 'Salvează'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
